import React, { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import { View, Pressable, FlatList, useWindowDimensions, ScrollView, Text, SectionList, Platform } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import styles from '@stylesheet';
import { PIN_NOTE, CLEAR_ALL_NOTES, SHOW_ADD_COMPANY_MODAL, NOTE_DROPPED, CONTEXT_SIZE_TOGGLE, SWITCH_COMPANY_REQUEST, MENU_RESIZE, SHOW_HELP_MODAL, TOGGLE_EASY_MODE, CLOSE_MENU, SHOW_FIRST_TUTORIAL, SKIP_ONBOARDING, START_ONBOARDING, CURRENT_PAGE } from '@reduxLocal/actions/types';
import { useToast } from '../../contexts/ToastContext';
import { FontAwesome6 } from '@expo/vector-icons';
import { useState as useStateAlias } from 'react';
import { Pressable as WebPressable } from 'react-native';
import { DefaultRootState, store } from '@reduxLocal/persistState';
import { MaterialIcons, MaterialCommunityIcons, FontAwesome, AntDesign } from '@expo/vector-icons';
import * as Animatable from 'react-native-animatable';
import { getCompanyList } from '@api/api';
import apis from '@api/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import RenderNote from '../screens/MidLevel/WelcomeScreen/Widgets/Components/RenderNote';
import StatusBar from '@navigation/StatusBar/statusBar';
import CompanySelector from './components/CompanySelector';
import { useNavigation, useNavigationState } from '@react-navigation/native';
import LottieView from 'lottie-react-native'
import NetworkStatusIndicator from './components/NetworkStatusIndicator';
import { useDeviceType } from '@hooks/useDeviceType';
import ContextProgressRing from './components/ContextProgressRing';

// Tooltip/Label component for hover states
const HoverLabel = ({ label, children, position = 'bottom' }: { label: string; children: React.ReactNode; position?: 'top' | 'bottom' | 'left' | 'right' }) => {
  const [isVisible, setIsVisible] = useState(false);

  const positionStyles = {
    top: { bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: 12 },
    bottom: { top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: 12 },
    left: { right: '100%', top: '50%', transform: 'translateY(-50%)', marginRight: 12 },
    right: { left: '100%', top: '50%', transform: 'translateY(-50%)', marginLeft: 12 },
  };

  return (
    <View style={{ position: 'relative', }}>
      <WebPressable
        onHoverIn={() => setIsVisible(true)}
        onHoverOut={() => setIsVisible(false)}
        style={{ position: 'relative' }}
      >
        {children}
        {isVisible && Platform.OS === 'web' && (
          <View style={[{
            position: 'absolute',
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            paddingHorizontal: 10,
            paddingVertical: 6,
            borderRadius: 6,
            zIndex: 1000,
            minWidth: 'max-content',
            whiteSpace: 'nowrap',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.25,
            shadowRadius: 4,
            elevation: 5,
            ...positionStyles[position]
          } as any]}>
            <Text style={{ color: 'white', fontSize: 13, fontWeight: '600' }}>
              {label}
            </Text>
            {/* Triangle arrow */}
            <View style={[{
              position: 'absolute',
              width: 0,
              height: 0,
              borderLeftWidth: 6,
              borderRightWidth: 6,
              borderLeftColor: 'transparent',
              borderRightColor: 'transparent',
              left: '50%',
              transform: 'translateX(-50%)',
              ...(position === 'bottom' ? {
                top: -6,
                borderBottomWidth: 6,
                borderBottomColor: 'rgba(0, 0, 0, 0.85)'
              } : {
                bottom: -6,
                borderTopWidth: 6,
                borderTopColor: 'rgba(0, 0, 0, 0.85)'
              })
            } as any]} />
          </View>
        )}
      </WebPressable>
    </View>
  );
};

export const textHelp = ['This feature allows you to manage your accounts and organisations. Simply create a new company and start adding users to boost start your business.', "Don't forget to add a logo to your shiny new company, that way it is easier to recognise when you are using it!", "Remember, a new company is a new subscription. You will be billed seperately for each company you create.", "You can switch between companies at any time by clicking on the company name in the top left corner of the screen.", "You can also add a new company by clicking on the plus icon next to the company name in the top left corner of the screen.", "You can also add a new company by clicking on the plus icon next to the company name in the top left corner of the screen."];

const HeaderBar: any = ({ feedbackRef }) => {
  const authenticated = useSelector((state: DefaultRootState) => state.promiseData.isAuthenticated);
  const theme = useSelector((state: DefaultRootState) => state.app.theme.lightTheme);

  // Get toast function for dev testing (only works on web, but hook must be called unconditionally)
  let showToast: ((message: string, actionLabel?: string, onAction?: () => void) => void) | null = null;
  try {
    const toastHook = useToast();
    if (Platform.OS === 'web') {
      showToast = toastHook.showToast;
    }
  } catch (e) {
    // Toast context not available (e.g., not wrapped in ToastProvider), ignore
    showToast = null;
  }
  // let { planType } = useSelector((state: DefaultRootState) => state.promiseData.data?.userData);
  const stuckNotes = useSelector((state: DefaultRootState) => state.app.stickyNotes.pinnedNotes);
  const stickyNotes = useSelector((state: DefaultRootState) => state.app.stickyNotes);
  // const activeStickyNote = useSelector((state: DefaultRootState) => state.app.stickyNotes.activeNote);
  const dispatch = useDispatch();
  const [hidden, setHidden] = useState(false);
  const contextAvailable = useSelector((state: DefaultRootState) => state.UI.contextAvailable);
  const paymentRequired = useSelector((state: DefaultRootState) => state.app.paymentRequired);
  const navigation = useNavigation();
  const easyMode = useSelector((state: DefaultRootState) => state.UI.easyMode);
  const onboardingInProgress = useSelector((state: DefaultRootState) => state.UI.onboardingInProgress);
  const learningMode = useSelector((state: DefaultRootState) => state.app.learningMode);
  const userTutorials = useSelector((state: DefaultRootState) => state.promiseData.data?.userData?.tutorials);
  const [searchVal, setSearchVal] = useState("");
  const [availableStatusBarWidth, setAvailableStatusBarWidth] = useState(0);
  const [searchExpanded, setSearchExpanded] = useState(false);

  // Get current navigation state to detect when we're on Welcome screen
  const navigationState = useNavigationState((state) => {
    // Find the Dashboard route and check its nested state
    const dashboardRoute = state?.routes?.find((r: any) => r.name === 'Dashboard');
    if (dashboardRoute?.state) {
      const currentScreen = dashboardRoute.state.routes?.[dashboardRoute.state.index || 0]?.name;
      return currentScreen;
    }
    return null;
  });

  // Automatically skip onboarding when navigating to home (Welcome) in easy mode
  // if onboarding hasn't been completed
  useEffect(() => {
    // Only run for authenticated users
    if (!authenticated) return;

    // Check if we're on Welcome screen, in easy mode, onboarding is in progress,
    // and tutorial hasn't been completed
    if (
      navigationState === 'Welcome' &&
      easyMode &&
      onboardingInProgress &&
      !userTutorials?.tutorialModal
    ) {
      // Automatically skip onboarding when navigating to home in easy mode
      // Update backend to mark tutorial modal as complete
      apis.setTutorial().catch((error) => {
        console.error('Error setting tutorial complete:', error);
      });
      // Update Redux state
      dispatch({ type: SKIP_ONBOARDING });
    }
  }, [navigationState, easyMode, onboardingInProgress, userTutorials?.tutorialModal, authenticated, dispatch]);

  // Responsive hooksv 
  const { width } = useWindowDimensions();

  // Responsive breakpoints
  const isSmall = width < 768;
  const isMedium = width >= 768 && width < 1024;
  const isLarge = width >= 1024 && width < 1440;
  const isXLarge = width >= 1440;

  // Responsive values
  const headerHeight = useMemo(() => {
    if (isSmall) return 50;
    if (isMedium) return 55;
    return 60;
  }, [isSmall, isMedium]);

  const iconSize = useMemo(() => {
    if (isSmall) return 28;
    if (isMedium) return 32;
    return 36;
  }, [isSmall, isMedium]);

  const shouldShowNotes = useMemo(() => {
    return authenticated && !isSmall && stuckNotes.length > 0;
  }, [authenticated, isSmall, stuckNotes.length]);

  // Calculate available space for status bar
  const calculateAvailableSpace = useCallback(() => {
    const menuButtonWidth = iconSize + 16; // button + padding
    const companyWidth = isSmall ? 120 : isMedium ? 150 : 200;
    const paymentWarningWidth = paymentRequired ? (isSmall ? 28 : 32) : 0;
    const notesWidth = shouldShowNotes ? (isLarge || isXLarge ? width * 0.4 : width * 0.35) : 0;
    const contextButtonWidth = contextAvailable ? (isSmall ? 35 : 40) : 0;
    const padding = isSmall ? width * 0.04 : isMedium ? width * 0.06 : width * 0.1;
    const gaps = isSmall ? 8 * 4 : isMedium ? 16 * 4 : 24 * 4; // gaps between elements

    const usedWidth = menuButtonWidth + companyWidth + paymentWarningWidth + 
                     notesWidth + contextButtonWidth + padding + gaps;
    
    return Math.max(0, width - usedWidth);
  }, [width, isSmall, isMedium, isLarge, isXLarge, iconSize, shouldShowNotes, 
      paymentRequired, contextAvailable]);

  // Update available space when dimensions change
  useEffect(() => {
    setAvailableStatusBarWidth(calculateAvailableSpace());
  }, [calculateAvailableSpace]);

  let timeoutId;

    const stuckNotesLength = () => {
      if (stuckNotes.length != 0) {
        return true
      }
      return false
    }


    const pinnedChecker = () => {
      if (stickyNotes.noteDragged && stickyNotes.pinnedNotes.length <= 3) {
        setTimeout(() => { dispatch({ type: PIN_NOTE, payload: { id: stickyNotes.activeNote.id, content: stickyNotes.activeNote.content } }); dispatch({ type: NOTE_DROPPED }) }, 300)
      } else if (stickyNotes.noteDragged) {
        alert('Cannot pin more than 4 notes!');
      }
    }

    const handleHoverIn = () => {
      if (!stuckNotes.length) return;
      setHidden(true);
      pinnedChecker();
      // Clear the timeout if it's set
      clearTimeout(timeoutId);
    };

    const handleHoverOut = () => {
      // Set a new timeout
      timeoutId = setTimeout(() => setHidden(false), 2500);
    };

    return (
      <View style={{
        flexDirection: 'row',
        alignSelf: "stretch",
        paddingHorizontal: isSmall ? "2%" : isMedium ? "3%" : "5%",
        alignContent: 'center',
        alignItems: 'center',
        paddingRight: "2%",
        backgroundColor: theme ? "white" : "#333333",
        zIndex: 100,
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        gap: isSmall ? 8 : isMedium ? 16 : 24,
        height: headerHeight,
        // Removed minWidth for better responsiveness
      }}>

      
        {/* Menu Button */}
        <Pressable
          id="tutorial-menu-button"
          style={{
            padding: 8,
            borderRadius: 8,
            width: iconSize + 16,
            marginRight: "1%",
            height: iconSize + 16,
            justifyContent: 'center',
            alignItems: 'center',
            opacity: onboardingInProgress ? 0.5 : 1
          }}
          disabled={onboardingInProgress}
          onPress={() => {
            if (onboardingInProgress) return; // Prevent resize during onboarding
            dispatch({ type: MENU_RESIZE });
          }}
        >
          <MaterialIcons
            name="menu-open"
            size={iconSize}
            color={theme ? "grey" : "white"}
          />
        </Pressable>
        {/* Company Selector */}
        <View style={{
          // flex: isSmall ? 0 : .1,
          minWidth: 150,
          backgroundColor: 'transparent'
        }}>
          <CompanySelector />
        </View>
  {/* Context Progress Ring */}
        {Platform.OS === 'web' && authenticated && (
          <View style={{ marginRight: isSmall ? 4 : 8 }}>
            <ContextProgressRing size={isSmall ? 40 : isMedium ? 45 : 50} />
          </View>
        )}
        {/* Learning Mode Indicator */}
        {learningMode && (
          <View style={{
            backgroundColor: theme ? '#3B82F6' : '#2563EB',
            borderRadius: 6,
            paddingHorizontal: isSmall ? 8 : 12,
            paddingVertical: isSmall ? 4 : 6,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            borderWidth: 1,
            borderColor: theme ? '#60A5FA' : '#3B82F6',
            marginRight: isSmall ? 4 : 8,
          }}>
            <FontAwesome6
              name="graduation-cap"
              size={isSmall ? 14 : 16}
              color="#ffffff"
              style={{ marginRight: 4 }}
            />
            <Text style={{
              color: '#ffffff',
              fontSize: isSmall ? 10 : 12,
              fontWeight: '600',
            }}>
              Learning Mode
            </Text>
          </View>
        )}

        {/* Easy/Normal Mode Toggle */}
        <HoverLabel label={onboardingInProgress ? "Complete onboarding to unlock" : "Switch into AI mode"} position="bottom">
          <View style={{
            flexDirection: 'row',
            alignItems: 'center',
            marginRight: isSmall ? 4 : 8,
            paddingHorizontal: isSmall ? 6 : 10,
            paddingVertical: isSmall ? 4 : 6,
            borderRadius: 20,
            backgroundColor: theme ? '#f0f0f0' : '#2a2a2a',
            opacity: onboardingInProgress ? 0.7 : 1,
          }}>
            <Text style={{
              fontSize: isSmall ? 11 : 12,
              fontWeight: '600',
              color: !easyMode ? (theme ? '#000' : '#fff') : (theme ? '#999' : '#666'),
              marginRight: 6,
            }}>
              Normal
            </Text>
            <Pressable
              id="tutorial-easy-mode-toggle"
              disabled={onboardingInProgress}
              onPress={async () => {
                if (onboardingInProgress) return; // Prevent toggle during onboarding
                // Check if EasyModeScreen has exposed a close handler
                if (easyMode && typeof window !== "undefined" && (window as any).__easyModeCloseHandler) {
                  const shouldClose = await (window as any).__easyModeCloseHandler();
                  if (!shouldClose) {
                    return; // User cancelled or dialog was dismissed
                  }
                  // Handler already dispatched TOGGLE_EASY_MODE, so we don't need to dispatch again
                } else {
                  // If activating easy mode (currently false), close the menu
                  if (!easyMode) {
                    dispatch({ type: CLOSE_MENU });
                    // Dispatch navigation item with no screen when easy mode is toggled on
                    dispatch({
                      type: CURRENT_PAGE,
                      payload: { route: { name: '', key: null, params: {} } }
                    });
                  }
                  dispatch({ type: TOGGLE_EASY_MODE });
                }
              }}
              style={{
                width: isSmall ? 36 : 42,
                height: isSmall ? 20 : 24,
                borderRadius: 12,
                backgroundColor: easyMode ? (theme ? '#5ac0ea' : '#4a9bc4') : (theme ? '#ccc' : '#555'),
                justifyContent: 'center',
                paddingHorizontal: 2,
                opacity: onboardingInProgress ? 0.5 : 1,
              }}
            >
              <View style={{
                width: isSmall ? 16 : 20,
                height: isSmall ? 16 : 20,
                borderRadius: 10,
                backgroundColor: '#fff',
                transform: [{ translateX: easyMode ? (isSmall ? 16 : 18) : 0 }],
              }} />
            </Pressable>
            <View style={{ flexDirection: 'row', alignItems: 'center', marginLeft: 6, }}>
              <Text style={{
                fontSize: isSmall ? 11 : 12,
                fontWeight: '600',
                color: easyMode ? (theme ? '#000' : '#fff') : (theme ? '#999' : '#666'),
              }}>
                Easy
              </Text>
              <Text style={{
                fontSize: isSmall ? 8 : 9,
                fontWeight: 'bold',
                color: 'white',
                backgroundColor: '#f0ad4e',
                paddingHorizontal: 4,
                paddingVertical: 1,
                borderRadius: 4,
                marginLeft: 5,
                overflow: 'hidden',
                textTransform: 'uppercase',
              }}>
                Beta
              </Text>
            </View>
            {onboardingInProgress && (
              <>
                <MaterialIcons
                  name="lock"
                  size={isSmall ? 14 : 16}
                  color={theme ? '#666' : '#999'}
                  style={{ marginLeft: 6 }}
                />
                <Pressable
                  onPress={async () => {
                    // Update backend to mark tutorial modal as complete
                    try {
                      await apis.setTutorial();
                    } catch (error) {
                      console.error('Error setting tutorial complete:', error);
                      // Continue even if API call fails - still update Redux
                    }
                  // Update Redux state
                    dispatch({ type: SKIP_ONBOARDING });
                  }}
                  style={{
                    marginLeft: 8,
                    paddingHorizontal: 8,
                    paddingVertical: 4,
                    borderRadius: 12,
                    backgroundColor: theme ? '#e0e0e0' : '#3a3a3a',
                  }}
                >
                  <Text style={{
                    fontSize: isSmall ? 10 : 11,
                    fontWeight: '600',
                    color: theme ? '#666' : '#ccc',
                  }}>
                    Skip Onboarding
                  </Text>
                </Pressable>
              </>
            )}
          </View>
        </HoverLabel>

        {/* Dev-only: Test First Tutorial Button */}
        {__DEV__ && Platform.OS === 'web' && (
          <>
            <Pressable
              onPress={() => {
                dispatch({ type: SHOW_FIRST_TUTORIAL });
              }}
              style={{
                marginRight: isSmall ? 4 : 8,
                paddingHorizontal: isSmall ? 8 : 12,
                paddingVertical: isSmall ? 4 : 6,
                borderRadius: 8,
                backgroundColor: theme ? '#e3f2fd' : '#1a237e',
              }}
            >
              <Text style={{
                fontSize: isSmall ? 10 : 11,
                fontWeight: '600',
                color: theme ? '#1976d2' : '#90caf9',
              }}>
                Test Tutorial
              </Text>
            </Pressable>
            <Pressable
              onPress={() => {
                // Ensure Easy Mode is enabled first
                if (!easyMode) {
                  dispatch({ type: TOGGLE_EASY_MODE });
                }
                // Start onboarding
                dispatch({ type: START_ONBOARDING });
              }}
              style={{
                marginRight: isSmall ? 4 : 8,
                paddingHorizontal: isSmall ? 8 : 12,
                paddingVertical: isSmall ? 4 : 6,
                borderRadius: 8,
                backgroundColor: theme ? '#fff3e0' : '#e65100',
              }}
            >
              <Text style={{
                fontSize: isSmall ? 10 : 11,
                fontWeight: '600',
                color: theme ? '#e65100' : '#ffb74d',
              }}>
                Test Onboarding
              </Text>
            </Pressable>
            {showToast && (
              <Pressable
                onPress={() => {
                  showToast(
                    'App update available. Click to reload.',
                    'Reload',
                    () => {
                      console.log('Test: User clicked reload button - reloading app...');
                      // Actually reload the app to test the full flow
                      if (Platform.OS === 'web' && typeof window !== 'undefined') {
                        window.location.reload();
                      }
                    }
                  );
                }}
                style={{
                  marginRight: isSmall ? 4 : 8,
                  paddingHorizontal: isSmall ? 8 : 12,
                  paddingVertical: isSmall ? 4 : 6,
                  borderRadius: 8,
                  backgroundColor: theme ? '#f0fdf4' : '#14532d',
                }}
              >
                <Text style={{
                  fontSize: isSmall ? 10 : 11,
                  fontWeight: '600',
                  color: theme ? '#16a34a' : '#86efac',
                }}>
                  Test Update Toast
                </Text>
              </Pressable>
            )}
          </>
        )}

        {/* Payment Required Warning */}
        {paymentRequired && (
          <Pressable
            onPress={() => {
              try {
                (navigation as any).navigate("Settings", { screen: "Upgrade" });
              } catch (error) {
                console.error('Navigation error:', error);
              }
            }}
            style={{
              width: isSmall ? 25 : 30,
              height: isSmall ? 30 : 35,
              maxWidth: isSmall ? 28 : 32,
              maxHeight: isSmall ? 30 : 35
            }}
          >
            <Animatable.View transition={['borderBottomEndRadius', 'borderBottomStartRadius', 'marginBottom', 'backgroundColor']} style={{
              borderRadius: 15,
              borderBottomEndRadius: 15,
              borderBottomStartRadius: 15,
              elevation: 5,
              shadowRadius: 5,
              padding: 2,
              backgroundColor: 'red',
              flex: 1,
              overflow: 'hidden',
              marginBottom: 5
            }}>
              <LottieView
                source={require("@assets/animations/exclamation-mark.json")}
                loop={false}
                autoPlay
                style={{
                  flex: 1,
                  left: isSmall ? -8 : -10.25,
                  height: isSmall ? 40 : 50,
                  top: isSmall ? -9 : -11.5,
                  width: isSmall ? 40 : 50
                }}
              />
            </Animatable.View>
          </Pressable>
        )}
        {/* Sticky Notes - Hidden on small screens */}
        {shouldShowNotes ? (
          <Animatable.View useNativeDriver animation={'slideInDown'} style={{
            flex: isLarge || isXLarge ? 4 : 3,
            flexDirection: "row",
            maxWidth: isLarge || isXLarge ? "40%" : "35%"
          }}>
            <WebPressable
              onHoverIn={handleHoverIn}
              onHoverOut={handleHoverOut}
              style={{
                flexDirection: 'row',
                flex: 1,
                overflow: 'hidden'
              }}
              hitSlop={0}
            >
              <Animatable.View transition={'marginTop'} style={{
                marginTop: hidden ? 200 : -200,
                flexDirection: 'row',
                paddingHorizontal: isLarge || isXLarge ? 30 : 15,
                flex: 1
              }}>
                {stuckNotesLength() && (
                  <WebPressable
                    onPress={() => { dispatch({ type: CLEAR_ALL_NOTES }) }}
                    style={{ marginTop: 10 }}
                  >
                    <MaterialCommunityIcons
                      name={"delete"}
                      size={isLarge || isXLarge ? 30 : 24}
                      color={'rgba(0,0,0,0.25)'}
                    />
                  </WebPressable>
                )}
                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                  {stuckNotes.map((note, i) => (
                    <RenderNote 
                      key={i} 
                      note={note} 
                      isStuck 
                      count={stuckNotes.length}
                      refetch={() => {}}
                      inputCallback={() => {}}
                    />
                  ))}
                </ScrollView>
              </Animatable.View>
            </WebPressable>
          </Animatable.View>
        ) : (<View style={{ flex: 4 }} />)}
          {/* Status Bar and Context Toggle */}
          <View style={{
            flexDirection: "row",
            alignItems: 'center',
          flex: .5,
          width: isSmall ? 100 : 150,
            justifyContent: 'flex-end',
            marginLeft: !isSmall ? 8 : 0 // Add spacing from community links
          }}>
          <StatusBar 
            availableWidth={availableStatusBarWidth}
            isSmallScreen={isSmall}
            searchVal={searchVal}
            setSearchVal={setSearchVal}
            searchExpanded={searchExpanded}
            setSearchExpanded={setSearchExpanded}
            onSearchSubmit={(query) => {
              // Open HelpModal with the search query
              dispatch({ type: SHOW_HELP_MODAL, payload: { query } });
              // Clear search and collapse
              setSearchVal("");
              setSearchExpanded(false);
            }}
          />
          <NetworkStatusIndicator/>            
        </View>
      </View>
    )
  }

export default HeaderBar;